import React, { useContext, useState } from 'react';
import {
  BubbleButton,
  BubbleFormGroup,
  BubbleInput,
  BubbleSelect,
  BubbleTypeahead,
} from '@go-client/components/BubbleUI';
import { FeedArea } from '@go-shared/types/feedTemplate';
import { FeedEditorCtx } from '@go-client/context/FeedEdtitorContext';
import { BrandAttribute } from '@go-shared/types/brand';
import { BubbleTypeaheadValue } from '@go-client/components/BubbleUI/BubbleTypeahead';
import { DailyProgramVenue } from '@go-shared/types/dailyProgram';
import { EventRundownTimeGrouping } from '@go-shared/types/contentTemplate';
import { Time } from '@go-shared/types/time';
import { minutesToTime, timeToMinutes } from '@go-shared/lib/time';

interface EventRundownEditorProps {
  feedArea: FeedArea;
  eventRundownId: string;
  onEventRundownUpdated: (updatedFeedArea: FeedArea) => void;
}

export const EventRundownEditor: React.FC<EventRundownEditorProps> = ({
  feedArea,
  onEventRundownUpdated,
}) => {
  const { dailyProgram, property } = useContext(FeedEditorCtx);

  const timeGroupingOpts = (property?.brand.timeGroupings ?? []).map(tg => tg.name);

  const [startOffset, setStartOffset] = useState(
    Math.abs(feedArea.content?.params?.startDateOffset ?? 0)
  );

  const [endOffset, setEndOffset] = useState(feedArea.content?.params?.endDateOffset ?? 0);
  const [title, setTitle] = useState(feedArea.content?.params?.eventRundownTitle ?? '');
  const [deckPrefix, setDeckPrefix] = useState(feedArea.content?.params?.eventRundownDeckPrefix);
  const [filterAttributes, setFilterAttributes] = useState(
    feedArea.content?.params?.eventRundownFilter?.attributes ?? []
  );
  const [filterVenues, setFilterVenues] = useState(
    feedArea.content?.params?.eventRundownFilter?.venues ?? []
  );
  const [showDeckPrefix, setShowDeckPrefix] = useState(
    feedArea.content?.params?.eventRundownDeckPrefix !== undefined
  );
  const [timeGrouping, setTimeGrouping] = useState<EventRundownTimeGrouping[]>(
    feedArea.content?.params?.eventRundownTimeGrouping ?? []
  );
  const [draftName, setDraftName] = useState('');
  const [draftTime, setDraftTime] = useState<Time | undefined>(undefined);
  const [draftTimeResetKey, setDraftTimeResetKey] = useState(0);

  if (!dailyProgram) {
    return <div className="p-4">No daily program available</div>;
  }

  function isValidDraftTimeGrouping(): boolean {
    return !!draftName && draftTime !== undefined;
  }

  function handleTimeGroupingAdd() {
    if (!isValidDraftTimeGrouping()) return;

    setTimeGrouping([...timeGrouping, { name: draftName, startTime: timeToMinutes(draftTime!) }]);
    setDraftName('');
    setDraftTime(undefined);
    setDraftTimeResetKey(k => k + 1);
  }

  function handleTimeGroupingDraftClear() {
    setDraftName('');
    setDraftTime(undefined);
    setDraftTimeResetKey(k => k + 1);
  }

  function getEffectiveTimeGrouping(): EventRundownTimeGrouping[] {
    if (!isValidDraftTimeGrouping()) return timeGrouping;

    return [...timeGrouping, { name: draftName, startTime: timeToMinutes(draftTime!) }];
  }

  function handleTimeGroupingDelete(index: number) {
    setTimeGrouping(timeGrouping.filter((_, i) => i !== index));
  }

  function setTimeGroupingName(index: number, name: string) {
    setTimeGrouping(timeGrouping.map((tg, i) => (i === index ? { ...tg, name } : tg)));
  }

  function setTimeGroupingStartTime(index: number, time: Time | undefined) {
    if (!time) return;
    setTimeGrouping(
      timeGrouping.map((tg, i) => (i === index ? { ...tg, startTime: timeToMinutes(time) } : tg))
    );
  }

  return (
    <>
      <div className="bg-eggshell">
        <div>
          <div className="p-4 grid gap-4 grid-cols-2 border-b">
            <BubbleFormGroup label="Title" className="col-span-2">
              <BubbleInput type="text" value={title} onValue={setTitle} block />
            </BubbleFormGroup>
            <BubbleFormGroup label="Days Before">
              <BubbleInput
                type="number"
                className="mr-2"
                inputClassName="bg-white"
                block
                min={0}
                value={startOffset}
                onValue={val => setStartOffset(Math.max(0, val))}
                id="days-before"
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Days After">
              <BubbleInput
                type="number"
                className="mr-2"
                inputClassName="bg-white"
                block
                min={0}
                value={endOffset}
                onValue={val => setEndOffset(Math.max(0, val))}
                id="days-after"
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Filter by Attributes" className="col-span-2">
              <BubbleTypeahead<BrandAttribute>
                placeholder="Search and add attributes..."
                block
                options={(property?.brand.attributes || []).map(attr => ({
                  key: attr._id,
                  item: attr,
                }))}
                value={filterAttributes
                  .map(id => {
                    const attr = property?.brand.attributes.find(a => a._id === id);
                    return attr
                      ? {
                          key: attr._id,
                          refKey: attr._id,
                          item: attr,
                        }
                      : undefined;
                  })
                  .filter((val): val is BubbleTypeaheadValue<BrandAttribute> => !!val)}
                labelExtractor={a => a.name}
                onAdd={val => {
                  if (!filterAttributes.includes(val.refKey)) {
                    setFilterAttributes([...filterAttributes, val.refKey]);
                  }
                }}
                onRemove={val => {
                  setFilterAttributes(filterAttributes.filter(id => id !== val.refKey));
                }}
                onClearAll={() => setFilterAttributes([])}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Filter by Venues" className="col-span-2">
              <BubbleTypeahead<DailyProgramVenue>
                placeholder="Search and add venues..."
                block
                options={(dailyProgram?.venues || []).map(v => ({
                  key: v.templateId,
                  item: v,
                }))}
                value={filterVenues
                  .map(tplId => {
                    const v = dailyProgram?.venues.find(v => v.templateId === tplId);
                    return v
                      ? {
                          key: v.templateId,
                          refKey: v.templateId,
                          item: v,
                        }
                      : undefined;
                  })
                  .filter((val): val is BubbleTypeaheadValue<DailyProgramVenue> => !!val)}
                labelExtractor={v => v.name}
                onAdd={val => {
                  if (!filterVenues.includes(val.refKey)) {
                    setFilterVenues([...filterVenues, val.refKey]);
                  }
                }}
                onRemove={val => {
                  setFilterVenues(filterVenues.filter(tplId => tplId !== val.refKey));
                }}
                onClearAll={() => setFilterVenues([])}
              />
            </BubbleFormGroup>

            <div className="col-span-2">
              <BubbleFormGroup label="Change Deck Prefix?">
                <BubbleInput
                  type="checkbox"
                  className="mr-2"
                  inputClassName="bg-white"
                  checked={showDeckPrefix}
                  onValue={checked => {
                    setShowDeckPrefix(checked);
                    if (!checked) setDeckPrefix(undefined);
                  }}
                  id="deck-prefix-toggle"
                />
              </BubbleFormGroup>
              {showDeckPrefix && (
                <BubbleFormGroup label="Deck Prefix" className="mt-2">
                  <BubbleInput type="text" value={deckPrefix} onValue={setDeckPrefix} block />
                </BubbleFormGroup>
              )}
            </div>
          </div>
          <div className="p-4 border-b">
            <BubbleFormGroup label="Time Grouping">
              <div
                className="max-w-sm mx-auto grid gap-2 items-center"
                style={{ gridTemplateColumns: '1fr 1fr auto' }}
              >
                <span>Name</span>
                <span>Start Time</span>
                <div></div>
                {timeGrouping.map((tg, index) => (
                  <React.Fragment key={index}>
                    <BubbleSelect
                      block
                      allowUndefined
                      options={timeGroupingOpts}
                      value={tg.name}
                      onValue={value => setTimeGroupingName(index, value ?? '')}
                    />
                    <BubbleInput
                      block
                      type="time"
                      value={minutesToTime(tg.startTime)}
                      onValue={time => setTimeGroupingStartTime(index, time)}
                    />
                    <BubbleButton
                      variant="navy"
                      className="p-2-i justify-self-start"
                      icon="minus"
                      onClick={() => handleTimeGroupingDelete(index)}
                    />
                  </React.Fragment>
                ))}
                <BubbleSelect
                  block
                  allowUndefined
                  options={timeGroupingOpts}
                  value={draftName || undefined}
                  onValue={value => setDraftName(value ?? '')}
                />
                <BubbleInput
                  key={draftTimeResetKey}
                  block
                  type="time"
                  value={draftTime}
                  onValue={setDraftTime}
                />
                <div className="flex gap-1 justify-self-start">
                  <BubbleButton
                    variant="navy"
                    className="p-2-i"
                    icon="plus"
                    disabled={!isValidDraftTimeGrouping()}
                    onClick={handleTimeGroupingAdd}
                  />
                  <BubbleButton
                    variant="slate"
                    inverse
                    className="p-2-i"
                    icon="eraser"
                    disabled={!draftName && draftTime === undefined}
                    onClick={handleTimeGroupingDraftClear}
                  />
                </div>
              </div>
            </BubbleFormGroup>
          </div>
        </div>
      </div>

      <div className="p-4 sticky bottom-0 bg-white border-t">
        <BubbleButton
          variant="navy"
          icon="rotate"
          onClick={() => {
            const hasAttrs = Array.isArray(filterAttributes) && filterAttributes.length > 0;
            const hasVenues = Array.isArray(filterVenues) && filterVenues.length > 0;
            const hasAnyFilter = hasAttrs || hasVenues;
            const effectiveTimeGrouping = getEffectiveTimeGrouping();

            const { eventRundownFilter: _ignored, ...restParams } = feedArea.content?.params ?? {};

            const updatedParams: any = {
              ...restParams,
              startDateOffset: -Math.abs(startOffset),
              endDateOffset: endOffset,
              eventRundownTitle: title,
              eventRundownDeckPrefix: showDeckPrefix ? deckPrefix ?? '' : undefined,
              eventRundownTimeGrouping:
                effectiveTimeGrouping.length > 0 ? effectiveTimeGrouping : undefined,
              ...(hasAnyFilter && {
                eventRundownFilter: {
                  ...(hasAttrs ? { attributes: filterAttributes } : {}),
                  ...(hasVenues ? { venues: filterVenues } : {}),
                },
              }),
            };

            onEventRundownUpdated({
              ...feedArea,
              content: feedArea.content
                ? { ...feedArea.content, params: updatedParams }
                : undefined,
            });

            if (isValidDraftTimeGrouping()) {
              setTimeGrouping(effectiveTimeGrouping);
              setDraftName('');
              setDraftTime(undefined);
              setDraftTimeResetKey(k => k + 1);
            }
          }}
        >
          Sync Changes to Feed
        </BubbleButton>
      </div>
    </>
  );
};
